// Reputifly Drive portal client. No login: the opaque ?token in the URL is the
// scope. Uploads go via XHR so we get real progress events.
import type { CollectionView } from '../types'
import { cleanReportPath } from './reportUrl'

// The link is /<slug>/<code> (clean + short). The code is the last path segment.
// Fall back to ?token=/?code= for back-compat. The API param stays "token".
function readCode(): string {
  const u = new URL(window.location.href)
  const q = u.searchParams.get('token') || u.searchParams.get('code')
  if (q) return q
  const parts = u.pathname.split('/').filter(Boolean)
  return parts.length ? parts[parts.length - 1] : ''
}
let TOKEN = readCode()
let PASSCODE = ''
let ACCESS = ''   // short-lived signed grant from /resolve — lets <img>/<a> file URLs fetch bytes without the passcode in the URL
let accTs = 0     // when ACCESS was last set — kept stable between polls so thumbnail src URLs don't churn (else every 12s poll refetches every image)

export const hasToken = () => TOKEN.length > 0
export const setPasscode = (p: string) => { PASSCODE = p }

// Only the token goes in the query string. The passcode rides a request header
// (so it never lands in nginx access logs / browser history); raw <img>/<a> file
// URLs, which can't send a header, use the signed `acc` grant instead.
function qs(extra: Record<string, string> = {}) {
  return new URLSearchParams({ token: TOKEN, ...extra }).toString()
}
function pcHeaders(h: Record<string, string> = {}): Record<string, string> {
  return PASSCODE ? { ...h, 'x-drive-passcode': PASSCODE } : h
}

export class ApiError extends Error {
  status: number
  code?: string
  constructor(status: number, code?: string) {
    super(code || `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

export async function resolveCollection(): Promise<CollectionView> {
  const res = await fetch('/api/portal/resolve?' + qs(), { headers: pcHeaders() })
  if (!res.ok) {
    let code: string | undefined
    try { code = (await res.json())?.error } catch { /* */ }
    throw new ApiError(res.status, code)
  }
  const data = await res.json()
  // The grant lives 6h; only adopt a fresh one on first load or ~hourly, so the <img> URLs
  // stay byte-identical between 12s polls (re-minting every poll churned every thumbnail src
  // → a refetch storm that could 429 the download bucket on an image-heavy passcode link).
  if (data?.access && (!ACCESS || Date.now() - accTs > 60 * 60 * 1000)) { ACCESS = data.access; accTs = Date.now() }
  return data
}

export const fileUrl = (id: string) => '/api/portal/file?' + qs(ACCESS ? { id, acc: ACCESS } : { id })

// A report is presented at a clean extensionless child of the current share
// URL. Passcode-cleared viewers carry the same short-lived access grant used by
// thumbnails; the reusable passcode never enters browser history.
export function reportUrl(): string {
  return cleanReportPath(window.location.pathname, ACCESS)
}

export async function deleteFile(id: string): Promise<void> {
  const res = await fetch('/api/portal/delete?' + qs({ id }), { method: 'POST', headers: pcHeaders() })
  if (!res.ok) {
    let code: string | undefined
    try { code = (await res.json())?.error } catch { /* */ }
    throw new ApiError(res.status, code)
  }
}

export interface AnswerValue { selected?: string[]; other_text?: string; text?: string }
export async function saveAnswer(questionId: string, value: AnswerValue): Promise<void> {
  const res = await fetch('/api/portal/answer?' + qs(), {
    method: 'POST',
    headers: pcHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ question_id: questionId, value }),
  })
  if (!res.ok) {
    let code: string | undefined
    try { code = (await res.json())?.error } catch { /* */ }
    throw new ApiError(res.status, code)
  }
}

export interface UploadResult { ok: true; name: string; size: number }

export function uploadFile(path: string, file: File, onProgress?: (frac: number, loaded: number, total: number) => void): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', '/api/portal/upload?' + qs({ path }))
    if (PASSCODE) xhr.setRequestHeader('x-drive-passcode', PASSCODE)
    // STALL guard, NOT a flat timeout: a large video legitimately takes minutes on a
    // phone uplink, so a fixed xhr.timeout would kill a slow-but-working upload. Instead
    // we abort only if NO bytes move for 90s (a genuinely dropped/stuck connection) → the
    // tray shows a retryable 'stalled' error and auto-retry resumes, never an infinite hang.
    let lastTick = Date.now()
    const stall = setInterval(() => { if (Date.now() - lastTick > 90000) { try { xhr.abort() } catch { /* */ } } }, 5000)
    const done = () => clearInterval(stall)
    if (xhr.upload) {
      xhr.upload.onprogress = (e) => { lastTick = Date.now(); if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total, e.loaded, e.total) }
    }
    xhr.onload = () => {
      done()
      let body: { error?: string; name?: string; size?: number } = {}
      try { body = JSON.parse(xhr.responseText) } catch { /* */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as UploadResult)
      else reject(new ApiError(xhr.status, body?.error))
    }
    xhr.onerror = () => { done(); reject(new ApiError(0, 'network')) }
    xhr.ontimeout = () => { done(); reject(new ApiError(0, 'timeout')) }
    xhr.onabort = () => { done(); reject(new ApiError(0, 'stalled')) }
    const fd = new FormData()
    fd.append('file', file)
    xhr.send(fd)
  })
}
